// hooks/useToastMutation.ts
import { useState, useCallback, createElement } from "react";
import { useMutation } from "convex/react";
import { FunctionReference, OptionalRestArgs } from "convex/server";
import { getErrorMessage } from "@/lib/utils";
import ConfirmActionBar from "@/components/table/confirm-action-bar";
import type { ActionStatus, SetActionStatus } from "@/components/table/bulk-action-bar";

export function useToastMutation<
  Mutation extends FunctionReference<"mutation", "public", any, any>,
>(
  mutation: Mutation,
  options?: {
    loadingMessage?: string;
    successMessage?: string | ((result: Mutation["_returnType"]) => string);
    errorMessage?: string | ((error: string) => string);
  },
) {
  const mutate = useMutation(mutation);
  const [status, setStatus] = useState<ActionStatus>({ type: "idle" });

  const execute = useCallback(
    async (...args: OptionalRestArgs<Mutation>): Promise<Mutation["_returnType"]> => {
      setStatus({ type: "loading", message: options?.loadingMessage ?? "Procesando..." });
      try {
        const result = await (mutate as any)(...args);
        const successMsg =
          typeof options?.successMessage === "function"
            ? options.successMessage(result)
            : options?.successMessage ?? "¡Completado!";
        setStatus({ type: "success", message: successMsg });
        return result;
      } catch (error) {
        const cleanError = getErrorMessage(error);
        const errorMsg =
          typeof options?.errorMessage === "function"
            ? options.errorMessage(cleanError)
            : options?.errorMessage ?? cleanError ?? "Error en la operación";
        setStatus({ type: "error", message: errorMsg });
        throw error;
      }
    },
    [mutate, options],
  );

  const bar = createElement(ConfirmActionBar, {
    status,
    onStatusChange: setStatus as SetActionStatus,
  });

  return { execute, mutate, bar, status, setStatus: setStatus as SetActionStatus };
}
